/**
 * Página Analizar — Analizador de Licitaciones (rediseño Quantia).
 *
 * Mantiene la lógica completa de:
 *   - Job polling y manejo de estado
 *   - Múltiples lotes con tabs
 *   - Edición manual de campos (fechas) detectados como pendientes
 *   - Descargas de Excel y PDF
 *
 * Solo se actualiza la presentación visual al sistema Quantia.
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import FileUpload from '../components/FileUpload';
import { SectionHeader, Badge } from '../components/quantia';
import {
  analyzeLicitacion,
  checkJobStatus,
  confirmAndSave,
  downloadExcel,
  downloadPdf,
  downloadFile,
} from '../services/api';
import { COLORS } from '../theme/theme';

const stepHeaderSx = {
  fontFamily: "'Fira Sans', sans-serif",
  fontWeight: 800,
  fontSize: '1.125rem',
  color: COLORS.black,
  mb: 0.5,
};
const stepDescSx = { color: COLORS.gray, fontSize: '0.875rem', mb: 2.5 };
const paperSx = {
  p: { xs: 2.5, md: 4 },
  mb: 3,
  borderRadius: 4,
  border: '1px solid rgba(91, 79, 240, 0.08)',
  backgroundColor: '#FFFFFF',
};

const Analizar = () => {
  const [files, setFiles] = useState([]);
  const [categoria] = useState('Consultoría');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentField, setCurrentField] = useState('');
  const [results, setResults] = useState(null);
  const [pdfFileName, setPdfFileName] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allData, setAllData] = useState(null);
  const [dataSaved, setDataSaved] = useState(false);
  const [comentarios, setComentarios] = useState('');
  const [currentLoteIndex, setCurrentLoteIndex] = useState(0);
  const [editedValues, setEditedValues] = useState({});
  const [editingFields, setEditingFields] = useState({});

  const handleFilesChange = (newFiles) => {
    setFiles(newFiles);
    setError('');
    setSuccess('');
  };

  const handleAnalyze = async () => {
    if (files.length === 0) {
      setError('Por favor, selecciona al menos un archivo PDF');
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);
    setError('');
    setSuccess('');
    setResults(null);
    setCurrentField('Iniciando análisis...');

    try {
      const response = await analyzeLicitacion(files, categoria, comentarios);
      const job_id = response.job_id;
      if (!job_id) throw new Error('No se recibió un ID de trabajo del servidor');
      setCurrentField('Análisis en cola...');

      const pollingInterval = setInterval(async () => {
        try {
          const status = await checkJobStatus(job_id);
          setProgress(status.progress || 0);
          setCurrentField(status.message || 'Procesando...');

          if (status.status === 'completed') {
            clearInterval(pollingInterval);
            setProgress(100);
            setCurrentField('¡Análisis completado!');

            if (status.result && status.result.data) {
              const allLotesData = status.result.all_data || [status.result.data];
              setAllData(allLotesData);
              setCurrentLoteIndex(0);
              setResults(allLotesData[0]);
              setDataSaved(false);

              if (status.result.pdf_file) {
                const pdfFilePath = status.result.pdf_file;
                const fileName = pdfFilePath.split('/').pop().split('\\').pop();
                setPdfFileName(fileName);
              }

              setSuccess(
                '¡Análisis completado! Revisa los datos y pulsa "Guardar y Descargar Excel" para confirmar.',
              );
            } else {
              setError('El análisis se completó pero no se recibieron datos');
            }
            setIsAnalyzing(false);
          } else if (status.status === 'error') {
            clearInterval(pollingInterval);
            setError(status.error || status.message || 'Error al procesar el análisis');
            setIsAnalyzing(false);
          }
        } catch (pollError) {
          clearInterval(pollingInterval);
          setError('Error al consultar el estado del análisis');
          setIsAnalyzing(false);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(pollingInterval);
        if (isAnalyzing) {
          setError('El análisis está tardando demasiado. Por favor, inténtalo nuevamente.');
          setIsAnalyzing(false);
        }
      }, 1200000);
    } catch (err) {
      setError(err.message || 'Error al analizar la licitación');
      setIsAnalyzing(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      if (allData && allData.length > 0) {
        setSuccess('Guardando datos en Excel...');
        const saveResult = await confirmAndSave(allData, categoria);
        if (!saveResult.success) {
          setError(saveResult.message || 'Error al guardar los datos en Excel');
          return;
        }
        setDataSaved(true);
      }
      const blob = await downloadExcel();
      downloadFile(blob, 'licitator.xlsx');
      setSuccess('Datos guardados y Excel descargado correctamente');
    } catch (err) {
      setError(err.message || 'Error al guardar/descargar el Excel');
    }
  };

  const handleDownloadPdf = async () => {
    if (!pdfFileName) {
      setError('No hay archivo PDF disponible para descargar');
      return;
    }
    try {
      const blob = await downloadPdf(pdfFileName);
      downloadFile(blob, pdfFileName);
      setSuccess('PDF descargado correctamente');
    } catch (err) {
      setError(err.message || 'Error al descargar el PDF');
    }
  };

  const handleChangeLote = (event, newIndex) => {
    if (allData && allData[newIndex]) {
      setCurrentLoteIndex(newIndex);
      setResults(allData[newIndex]);
      setEditedValues({});
      setEditingFields({});
    }
  };

  const getResultsArray = () => {
    if (!results) return [];
    return Object.entries(results).map(([campo, valor]) => ({
      campo,
      valor: valor || '-',
    }));
  };

  const necesitaEdicionManual = (campo, valor) => {
    const camposFecha = [
      'fecha',
      'plazo',
      'presentación',
      'presentacion',
      'límite',
      'limite',
      'apertura',
      'vencimiento',
    ];

    const esCampoFecha = camposFecha.some((keyword) =>
      campo.toLowerCase().includes(keyword),
    );

    if (valor && typeof valor === 'string' && valor.includes('INTRODUCIR MANUALMENTE')) {
      return true;
    }

    if (esCampoFecha) {
      const valorVacio =
        !valor ||
        valor === '-' ||
        valor === 'N/A' ||
        valor === 'No disponible' ||
        valor.trim() === '';
      return valorVacio;
    }

    return false;
  };

  const extraerMensajeAviso = (campo, valor) => {
    if (valor && typeof valor === 'string' && valor.includes('INTRODUCIR MANUALMENTE')) {
      return valor;
    }
    return `No se detectó ${campo} — Introducir manualmente`;
  };

  const handleManualInputChange = (campo, nuevoValor) => {
    setEditingFields((prev) => ({
      ...prev,
      [campo]: nuevoValor,
    }));
  };

  const handleGuardarValorManual = (campo) => {
    const nuevoValor = editingFields[campo];
    if (nuevoValor && nuevoValor.trim()) {
      setResults((prev) => ({
        ...prev,
        [campo]: nuevoValor.trim(),
      }));
      setEditedValues((prev) => ({
        ...prev,
        [campo]: nuevoValor.trim(),
      }));
      setEditingFields((prev) => {
        const newState = { ...prev };
        delete newState[campo];
        return newState;
      });
      setSuccess(`Campo "${campo}" actualizado correctamente`);
    }
  };

  const getValorMostrar = (campo, valorOriginal) => {
    if (editedValues[campo]) {
      return editedValues[campo];
    }
    return valorOriginal;
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Cabecera */}
      <SectionHeader
        label="02 — Análisis"
        title="Analizador de Licitaciones por IA"
        highlight="Licitaciones"
        description="Sube los pliegos en PDF y deja que QUANTIA Licitator extraiga automáticamente plazos, criterios, requisitos de solvencia, valor estimado y hasta 19 campos clave de información estructurada."
        sx={{ mb: 5 }}
      />

      {/* Paso 1: Subida */}
      <Paper sx={paperSx}>
        <Typography sx={stepHeaderSx}>📤 Paso 1 · Sube los documentos PDF</Typography>
        <Typography sx={stepDescSx}>
          Selecciona uno o varios archivos PDF de la licitación que quieras analizar.
        </Typography>
        <FileUpload
          onFilesChange={handleFilesChange}
          acceptedTypes=".pdf"
          multiple
          label="Arrastra los PDFs aquí o haz clic para seleccionarlos"
        />
      </Paper>

      {/* Paso 2: Comentarios */}
      {files.length > 0 && (
        <Paper sx={paperSx}>
          <Typography sx={stepHeaderSx}>💬 Paso 2 · Instrucciones adicionales (opcional)</Typography>
          <Typography sx={stepDescSx}>
            Indica el lote concreto, qué información priorizar o cualquier matiz relevante para el análisis.
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Ej: Solo necesito la información del Lote 2 — Consultoría técnica…"
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
          />
        </Paper>
      )}

      {/* Botón analizar */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              isAnalyzing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            fullWidth
          >
            {isAnalyzing ? 'Analizando…' : '🚀 Analizar licitación'}
          </Button>
        </Box>
      )}

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Progreso */}
      {isAnalyzing && (
        <Paper sx={paperSx}>
          <Typography sx={stepHeaderSx}>⏳ Progreso del análisis</Typography>

          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: 'rgba(91, 79, 240, 0.05)',
              borderRadius: 2,
              borderLeft: `4px solid ${COLORS.primary}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Fira Sans', sans-serif",
                fontWeight: 700,
                color: COLORS.primary,
                mb: 0.25,
              }}
            >
              {currentField || 'Iniciando análisis…'}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.gray }}>
              El análisis puede tardar varios minutos según el tamaño del documento.
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: COLORS.gray }}>
              Progreso general
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.primary }}>
              {progress}% completado
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              color: COLORS.gray,
            }}
          >
            💡 El sistema está extrayendo y analizando los 19 campos clave del documento.
          </Typography>
        </Paper>
      )}

      {/* Resultados */}
      {results && (
        <Paper sx={paperSx}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <Typography sx={stepHeaderSx}>📊 Resultados del análisis</Typography>
            <Badge variant="success" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}>
              Análisis completado
            </Badge>
          </Box>

          {/* Lotes */}
          {allData && allData.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                📦 Esta licitación tiene <strong>{allData.length} lotes</strong>. Selecciona una pestaña
                para ver los datos de cada uno. Al guardar, se almacenan TODOS en filas separadas del Excel.
              </Alert>
              <Tabs
                value={currentLoteIndex}
                onChange={handleChangeLote}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: `1px solid rgba(91, 79, 240, 0.12)` }}
              >
                {allData.map((loteData, idx) => {
                  const nombreCliente = loteData['Nombre del cliente'] || `Lote ${idx + 1}`;
                  const tabLabel =
                    nombreCliente.length > 40
                      ? nombreCliente.substring(0, 40) + '…'
                      : nombreCliente;
                  return <Tab key={idx} label={tabLabel} />;
                })}
              </Tabs>
            </Box>
          )}

          {/* Tabla de campos (con edición manual cuando aplica) */}
          <TableContainer
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(91, 79, 240, 0.12)',
              mb: 3,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '32%' }}>Campo</TableCell>
                  <TableCell>Valor extraído</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getResultsArray().map((row, index) => {
                  const valorMostrar = getValorMostrar(row.campo, row.valor);
                  const requiereEdicion = necesitaEdicionManual(row.campo, valorMostrar);

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: requiereEdicion
                          ? 'rgba(245, 158, 11, 0.08)'
                          : index % 2 === 0
                            ? 'transparent'
                            : 'rgba(91, 79, 240, 0.02)',
                        ...(requiereEdicion && {
                          borderLeft: `4px solid ${COLORS.warning}`,
                        }),
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "'Fira Sans', sans-serif",
                          fontWeight: 700,
                          color: COLORS.black,
                          verticalAlign: 'top',
                        }}
                      >
                        {row.campo}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', color: COLORS.textPrimary }}>
                        {requiereEdicion ? (
                          <Box>
                            {/* Aviso de edición manual */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1.5,
                                px: 1.5,
                                py: 1,
                                color: '#92400E',
                                backgroundColor: '#FFFBEB',
                                border: '1px solid #FDE68A',
                                borderRadius: 2,
                              }}
                            >
                              <WarningIcon sx={{ fontSize: 18 }} />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
                              >
                                {extraerMensajeAviso(row.campo, valorMostrar)}
                              </Typography>
                            </Box>

                            {/* Input + botón guardar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                placeholder="DD/MM/AAAA HH:MM"
                                value={editingFields[row.campo] || ''}
                                onChange={(e) =>
                                  handleManualInputChange(row.campo, e.target.value)
                                }
                                sx={{ flex: 1, maxWidth: 280 }}
                              />
                              <Tooltip title="Guardar valor">
                                <span>
                                  <IconButton
                                    onClick={() => handleGuardarValorManual(row.campo)}
                                    disabled={!editingFields[row.campo]?.trim()}
                                    sx={{
                                      backgroundColor: COLORS.primary,
                                      color: '#FFFFFF',
                                      borderRadius: 2.5,
                                      width: 40,
                                      height: 40,
                                      '&:hover': {
                                        backgroundColor: COLORS.primaryDark,
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: COLORS.grayLight,
                                        color: COLORS.gray,
                                      },
                                    }}
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        ) : (
                          valorMostrar
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Descarga */}
          <Typography sx={stepHeaderSx}>💾 Descargar resultados</Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ExcelIcon />}
              onClick={handleDownloadExcel}
              sx={{ flex: 1 }}
            >
              Guardar y descargar Excel
            </Button>
            {pdfFileName && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPdf}
                sx={{ flex: 1 }}
              >
                Descargar PDF resumen
              </Button>
            )}
          </Stack>

          <Alert severity="info">
            💾 Revisa los datos y completa los campos marcados como pendientes. Al pulsar
            "Guardar y descargar Excel" se guardarán todos los datos (incluidas tus ediciones).
            {dataSaved && ' ✓ Datos sincronizados.'}
          </Alert>
        </Paper>
      )}

      {/* Info inicial */}
      {!results && !isAnalyzing && (
        <Paper sx={{ ...paperSx, backgroundColor: 'rgba(91, 79, 240, 0.04)' }}>
          <Typography sx={stepHeaderSx}>ℹ️ Información del Analizador</Typography>
          <Typography sx={{ color: COLORS.gray, mb: 2 }}>
            Sube tus <strong>PDFs de licitaciones públicas</strong> y obtendrás:
          </Typography>
          <Stack spacing={1.25} sx={{ pl: 1 }}>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
              📅 <strong>Plazos clave:</strong> fechas límite, aperturas y periodos de presentación.
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
              📋 <strong>Requisitos de solvencia:</strong> económicos, técnicos y profesionales.
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
              ⚖️ <strong>Criterios de valoración:</strong> ponderaciones, fórmulas y condiciones.
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
              ✨ <strong>Y mucho más:</strong> todo listo para análisis y seguimiento.
            </Typography>
          </Stack>
        </Paper>
      )}
    </Container>
  );
};

export default Analizar;
